/** /tour-operators/:id/sandbox — the operator's sandbox environment (dev-platform metaplan S5b; SBX plan
 *  D3–D8, P3 console list).
 *
 *  - On a sandbox: its State (suspension, the circuit breaker, the booking quota's "lane paused",
 *    the ingress mode, the vendor key, the scheduled pull), its Admins (who production's "Open
 *    sandbox" signs in as; "Add admin"), its Outbound mode and allowlist, its SMS number, its
 *    Quotas, Reset, what was copied from production, and the config differences from production.
 *  - On a production operator with a sandbox: the pair and the config differences.
 *  - On one without: "Add sandbox".
 *
 *  Reading needs `console_sandbox_view` (the tab is not drawn without it); every change needs
 *  `console_sandbox_manage` and is audited server-side. */
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { sandboxErrorMessage } from '@/api/sandbox';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { Section } from '@/features/partners/PartnerSection';
import { usePermissions } from '@/hooks/usePermissions';

import { AddSandboxSection } from './AddSandboxSection';
import { ConfigDiffSection } from './ConfigDiffSection';
import { OutboundSection } from './OutboundSection';
import { QuotasSection } from './QuotasSection';
import { CloneReport, ResetSection } from './ResetSection';
import { SandboxAdminsSection } from './SandboxAdminsSection';
import { SandboxStateSection } from './SandboxStateSection';
import { SmsSection } from './SmsSection';
import { isSandbox, useSandboxState } from './useSandboxState';

export function SandboxTab({ to }: { to: TourOperatorDetail }) {
  const { hasPerm, hasAll } = usePermissions();
  if (isSandbox(to)) return <SandboxView to={to} canManage={hasPerm('console_sandbox_manage')} />;
  if (to.sandbox) return <ProductionPairView to={to} />;
  return (
    <Stack spacing={2} sx={{ maxWidth: 1100, pb: 4 }}>
      <Section title="Add sandbox">
        <AddSandboxSection
          to={to}
          canCreate={hasAll(['console_sandbox_manage', 'console_top_manage'])}
        />
      </Section>
    </Stack>
  );
}

function SandboxView({ to, canManage }: { to: TourOperatorDetail; canManage: boolean }) {
  const query = useSandboxState(to);
  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return (
      <Alert severity="error">
        {sandboxErrorMessage(query.error, 'Couldn’t load the sandbox state.')}
      </Alert>
    );
  }
  const s = query.data;
  const production =
    to.sandbox_of ??
    (s.sandbox_of?.id ? { id: s.sandbox_of.id, code: s.sandbox_of.code ?? '', name: '' } : null);
  return (
    <Stack spacing={2} sx={{ maxWidth: 1100, pb: 4 }}>
      <Section
        title="State"
        actions={
          production && (
            <Button size="small" component={RouterLink} to={`/tour-operators/${production.id}`}>
              Open production ({production.code})
            </Button>
          )
        }
      >
        {s.ingress_base_url && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Ingress base URL:{' '}
            <ValueActions actions={[act.copy(s.ingress_base_url, 'Copy the ingress base URL')]}>
              <Box
                component="span"
                sx={{ fontFamily: 'monospace', color: 'text.primary', wordBreak: 'break-all' }}
              >
                {s.ingress_base_url}
              </Box>
            </ValueActions>
          </Typography>
        )}
        <SandboxStateSection topId={to.id} state={s} canManage={canManage} />
      </Section>

      {/* A backend older than DSEC D6 sends no `admins`: it has no list and no "Add admin" either. */}
      {s.admins !== undefined && (
        <Section title="Admins">
          <SandboxAdminsSection
            topId={to.id}
            code={to.code}
            admins={s.admins}
            canManage={canManage}
          />
        </Section>
      )}

      <Section title="Outbound">
        <OutboundSection topId={to.id} state={s} canManage={canManage} />
      </Section>

      <Section title="SMS">
        <SmsSection
          topId={to.id}
          code={to.code}
          sms={s.sms}
          suspended={Boolean(s.suspension?.suspended)}
          canManage={canManage}
        />
      </Section>

      {s.quotas && (
        <Section title="Quotas">
          <QuotasSection topId={to.id} quotas={s.quotas} canManage={canManage} />
        </Section>
      )}

      <Section title="Reset">
        <ResetSection
          topId={to.id}
          code={to.code}
          reset={s.reset}
          suspended={Boolean(s.suspension?.suspended)}
          canManage={canManage}
        />
      </Section>

      {s.clone && (
        <Section title="Copied from production">
          <CloneReport clone={s.clone} />
        </Section>
      )}

      {production && (
        <Section title={`Config differences from ${production.code}`}>
          <ConfigDiffSection topId={to.id} />
        </Section>
      )}
    </Stack>
  );
}

function ProductionPairView({ to }: { to: TourOperatorDetail }) {
  const box = to.sandbox!;
  const { hasPerm } = usePermissions();
  return (
    <Stack spacing={2} sx={{ maxWidth: 1100, pb: 4 }}>
      <Section title="Sandbox">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ScienceOutlinedIcon sx={{ color: 'text.secondary' }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {box.name || box.code}{' '}
              <Box
                component="span"
                sx={{ fontFamily: 'monospace', fontWeight: 400, color: 'text.secondary' }}
              >
                {box.code}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {to.code}’s sandbox: a tenant of its own, where its integration is built and tested.
              Its state, outbound allowlist, quotas and reset are on its own page.
            </Typography>
          </Box>
          {/* Its readiness scorecard: what a person checks before unlocking production. */}
          {hasPerm('console_integration_readiness_view') && (
            <Button component={RouterLink} to={`/tour-operators/${box.id}/readiness`}>
              Readiness
            </Button>
          )}
          <Button
            component={RouterLink}
            to={`/tour-operators/${box.id}/sandbox`}
            variant="outlined"
          >
            Open sandbox
          </Button>
        </Box>
      </Section>
      <Section title={`Config differences from ${box.code}`}>
        <ConfigDiffSection topId={to.id} />
      </Section>
    </Stack>
  );
}
